import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { ActionButton as FluentActionButton, IButtonProps, IButtonStyles, IconButton as FluentIconButton, Spinner, SpinnerSize } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../../shared/logging";
import { validateProps } from "../../../shared/props";
import { getColorFromCorrectPalette } from "../../../styles/colors";
import { Icon } from "../../Icon/Icon";
import { TooltipHost, TooltipHostProps } from "../../TooltipHost/TooltipHost";
import { BaseIconButtonProps, OnClickButtonProps } from "../shared/props";

export interface IconButtonOperationDataProps {
    /**
     * Operation data needed to present IconButton in different states.
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;
}

export type IconButtonProps = BaseIconButtonProps & OnClickButtonProps & {
    readonly operationData?: IconButtonOperationDataProps;

    /** Props for tooltip */
    readonly tooltipHostProps?: TooltipHostProps

    /** override style when showing in different components. */
    readonly iconButtonVariant?: IconButtonVariant;
};

export type IconButtonVariant = "card" | "default";

export const IconButton: React.FC<IconButtonProps> = ({
    onClick,
    "aria-label": ariaLabel,
    disabled = false,
    iconName,
    iconColor = "themePrimary",
    hasHighContrastBackground,
    iconButtonVariant = "default",
    ...otherProps
}) => {
    useComponentLogger("IconButton", otherProps.id);

    validateProps("IconButton", otherProps);

    const theme = useTheme();

    const buttonProps = {
        ...otherProps,
        onClick,
        disabled,
        ariaLabel,
        onRenderIcon: () => (
            <Icon
                size="16px"
                color={disabled ? "neutralTertiaryAlt" : iconColor}
                iconName={iconName}
            />
        )
    };

    const computedColor = getColorFromCorrectPalette(theme, iconColor);
    const iconStyle: Partial<IButtonStyles> = {
        icon: {
            color: computedColor
        }
    };

    if (iconButtonVariant === "card") {
        iconStyle.root = {
            padding: "22px"
        };
    }

    const finishedView = () => {
        if (otherProps.tooltipHostProps) {
            return (
                <TooltipHost {...otherProps.tooltipHostProps}>
                    <FluentIconButton {...buttonProps} styles={iconStyle} />
                </TooltipHost>
            );
        }
        return <FluentIconButton {...buttonProps} styles={iconStyle} />;
    };

    if (disabled) {
        iconStyle.rootDisabled = { backgroundColor: "inherit" };
    }

    if (hasHighContrastBackground) {
        iconStyle.rootHovered = { backgroundColor: theme.palette.neutralLight };
        iconStyle.rootPressed = { backgroundColor: theme.palette.neutralQuaternaryAlt };
    }

    if (otherProps.operationData) {
        const { operationData } = otherProps;

        const loadingButtonProps: IButtonProps = {
            ...buttonProps,
            onClick: () => undefined,
            disabled: true,
            onRenderIcon: () => <Spinner color={computedColor} size={SpinnerSize.small} style={{ marginLeft: "4px", marginRight: "4px" }} />
        };

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                inertView={(
                    <FluentIconButton {...buttonProps} styles={iconStyle} />
                )}
                updatingView={(
                    <FluentActionButton {...loadingButtonProps} />
                )}
                finishedView={finishedView()}
            />
        );
    } else {
        return finishedView();
    }
};
